import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Loader2, Save } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { useLanguage } from '../context/LanguageContext';

// Company facts (from the old "Visuell" tab's CompanyEditor) — companyName,
// industry, coreActivities, targetAudience. Self-contained: own GET/PUT to
// /settings/company-profile (writes raw_json.<field>; PUT only $sets the sent
// fields, so unsent fields like `usps` stay untouched).
//
// NOTE: the old `usps` editor is intentionally DROPPED — brandDNA_structured
// .brand_identity.usp (edited via the Hybrid chat) is now the single source of
// truth for USP. Data migration of legacy company_profiles.usps happens later.
const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const BrandCompanyEditor = ({ getAuthHeader }) => {
  const { t } = useLanguage();
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [coreActivities, setCoreActivities] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/settings/company-profile`, { headers: getAuthHeader() });
      const raw = r.data?.raw_json || {};
      setCompanyName(raw.companyName || '');
      setIndustry(raw.industry || '');
      setCoreActivities(raw.coreActivities || '');
      setTargetAudience(raw.targetAudience || '');
    } catch { } finally { setLoaded(true); }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const mark = (setter) => (val) => { setter(val); setDirty(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/company-profile`,
        { fields: { companyName, industry, coreActivities, targetAudience } },
        { headers: getAuthHeader() });
      setDirty(false);
      toast.success(t?.settings?.company?.savedToast || 'Unternehmensdaten gespeichert');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  return (
    <Card className="bg-[#1A2B42] border-transparent">
      <CardHeader className="pb-3">
        <CardTitle className="text-white text-base flex items-center gap-2">
          <Building2 className="w-4 h-4 text-[#00D9C8]" />{t?.settings?.company?.companyDataTitle || 'Unternehmensdaten'}
        </CardTitle>
        <CardDescription className="text-[#A0AEC0] text-sm">{t?.settings?.company?.urlAnalyzeDesc || ''}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-end min-h-[8px]">
          {dirty && (
            <Button onClick={handleSave} disabled={saving}
              className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] h-8 px-4 text-xs" data-testid="save-company-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
              {t?.common?.save || 'Speichern'}
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-xs text-[#A0AEC0]">{t?.settings?.company?.name || 'Name'}</label>
            <Input value={companyName} onChange={(e) => mark(setCompanyName)(e.target.value)}
              className="bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white" data-testid="company-name-input" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs text-[#A0AEC0]">{t?.settings?.company?.industry || 'Branche'}</label>
            <Textarea value={industry} onChange={(e) => mark(setIndustry)(e.target.value)} rows={3}
              className="bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white resize-none overflow-y-auto" data-testid="company-industry-input" />
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs text-[#A0AEC0]">{t?.settings?.company?.activities || 'Kerntätigkeit'}</label>
          <Textarea value={coreActivities} onChange={(e) => mark(setCoreActivities)(e.target.value)} rows={3}
            className="bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white resize-none overflow-y-auto" data-testid="company-activities-input" />
        </div>

        <div className="space-y-1.5">
          <label className="text-xs text-[#A0AEC0]">{t?.settings?.company?.targetAudience || 'Zielgruppe'}</label>
          <Textarea value={targetAudience} onChange={(e) => mark(setTargetAudience)(e.target.value)} rows={3}
            className="bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white resize-none overflow-y-auto" data-testid="company-audience-input" />
        </div>
      </CardContent>
    </Card>
  );
};

export default BrandCompanyEditor;
